<template>
  <SeranovaModal
    data-cy="pay-modal"
    size="large"
    body-padding="none"
    show-close-button
    :model-value="true"
    @update:model-value="(v: boolean) => !v && $emit('hide-modal')"
  >
    <template #header>
      <div
        class="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-gray-700"
        data-cy="modal-title-row"
      >
        <strong class="font-semibold text-gray-900"
          >Invoice #{{ invoice?.invoice_id }}</strong
        >
        <span class="text-gray-400">·</span>
        <span>{{ billingCustomer?.full_name }}</span>
        <span v-if="balanceCents > 0" class="text-gray-400">·</span>
        <span
          v-if="balanceCents > 0"
          class="font-mono text-xs tabular-nums text-gray-900 bg-gray-100 px-2 py-0.5 rounded"
          data-cy="pay-modal-balance-due"
        >
          {{ dollars(balanceCents) }} due
        </span>
      </div>
    </template>

    <div
      v-if="billingCustomer"
      class="acceptPaymentV2 flex flex-col min-w-0 divide-y divide-gray-200 [&>*]:px-5 [&>*]:py-3"
    >
      <section data-cy="pay-modal-amount-section" aria-label="Payment amount">
        <div class="block text-sm font-medium text-gray-900 mb-2">Amount</div>
        <div
          class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_230px] gap-4 md:gap-6 items-start"
        >
          <div>
            <MoneyAmountField
              v-model="paymentAmountCents"
              tone="pay"
              data-cy="pay-modal-amount-input"
              :hint="amountHint"
            >
              <template #hint>
                <span>{{ amountHint }}</span>
                <button
                  v-if="
                    progressBilling && paymentAmountCents !== currentDrawCents
                  "
                  type="button"
                  class="text-xs font-semibold underline underline-offset-2 text-blue-700"
                  data-cy="pay-modal-fill-draw"
                  @click="paymentAmountCents = currentDrawCents"
                >
                  Use draw amount
                </button>
                <button
                  v-if="balanceCents > 0 && paymentAmountCents !== balanceCents"
                  type="button"
                  class="text-xs font-semibold underline underline-offset-2 text-blue-700"
                  data-cy="pay-modal-fill-balance"
                  @click="paymentAmountCents = balanceCents"
                >
                  Use full balance ({{ dollars(balanceCents) }})
                </button>
              </template>
            </MoneyAmountField>

            <InlineNote
              v-if="amountWarning"
              tone="warn"
              data-cy="pay-modal-amount-warning"
              class="mt-3"
            >
              <!-- amountWarning is composed from hardcoded copy + dollars()
                   output; no user input flows in, so v-html is safe here. -->
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="amountWarning" />
            </InlineNote>

            <div
              v-if="hasApplyableCredit"
              class="mt-3"
              data-cy="pay-modal-apply-credit-row"
            >
              <Toggle
                :checked="applyCredit"
                :disabled="mustApplyCredit"
                :label-after="`Apply ${dollars(
                  applyableCreditCents
                )} unallocated credit to this draw`"
                @update:checked="(v) => (applyCredit = v)"
              />
            </div>
          </div>

          <aside
            v-if="progressBilling"
            class="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-xs space-y-1 mr-2"
            data-cy="pay-modal-spb-card"
          >
            <div class="text-sm font-medium text-gray-900 flex justify-between">
              <span>Current draw</span>
              <span v-if="progressBilling.current_draw.label">{{
                progressBilling.current_draw.label
              }}</span>
            </div>
            <div class="flex justify-between text-gray-700">
              <span>This draw</span>
              <strong
                class="font-mono tabular-nums text-gray-900 font-medium"
                data-cy="pay-modal-spb-current-draw"
                >{{ dollars(currentDrawCents) }}</strong
              >
            </div>
            <div
              v-if="progressBilling.current_draw.due_date"
              class="flex justify-between text-gray-700"
            >
              <span>Due</span>
              <strong
                class="font-mono tabular-nums text-gray-900 font-medium"
                >{{
                  formatSpbDueDate(progressBilling.current_draw.due_date)
                }}</strong
              >
            </div>
            <hr class="border-gray-200 my-1.5" />
            <div class="flex justify-between text-gray-700">
              <span>Contract total</span>
              <strong
                class="font-mono tabular-nums text-gray-900 font-medium"
                >{{ dollars(progressBilling.contract_total_cents) }}</strong
              >
            </div>
            <div class="flex justify-between text-gray-700">
              <span>Paid to date</span>
              <strong
                class="font-mono tabular-nums text-gray-900 font-medium"
                >{{ dollars(progressBilling.paid_to_date_cents) }}</strong
              >
            </div>
            <div class="flex justify-between text-gray-700">
              <span>Remaining</span>
              <strong
                class="font-mono tabular-nums text-gray-900 font-medium"
                >{{
                  dollars(progressBilling.total_balance_remaining_cents)
                }}</strong
              >
            </div>
          </aside>
          <aside
            v-else
            class="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-xs space-y-1 mr-2"
            data-cy="pay-modal-balance-card"
          >
            <div class="text-sm font-medium text-gray-900">Invoice</div>
            <div class="flex justify-between text-gray-700">
              <span>Total</span>
              <strong
                class="font-mono tabular-nums text-gray-900 font-medium"
                >{{ dollars(invoice.financials.total_cents) }}</strong
              >
            </div>
            <div class="flex justify-between text-gray-700">
              <span>Balance</span>
              <strong
                class="font-mono tabular-nums text-gray-900 font-medium"
                >{{ dollars(balanceCents) }}</strong
              >
            </div>
          </aside>
        </div>
      </section>

      <section data-cy="pay-modal-method-section" aria-label="Payment method">
        <div class="block text-sm font-medium text-gray-900 mb-2">Method</div>

        <Spinner v-if="loadingMethods" message="Loading payment methods…" />

        <template v-else>
          <MethodTileGroup
            v-model="paymentType"
            :methods="methodTiles"
            data-cy="pay-modal-methods"
            aria-label="Payment method"
          />

          <!-- Edge-to-edge divider between the method picker and the
               selected method's form. -mx-5/px-5 cancels the parent
               section's horizontal padding so the rule lines up with the
               other section dividers in the modal. -->
          <div class="mt-3 min-w-0 -mx-5 px-5 pt-3 border-t border-gray-200">
            <PaymentMethods
              v-if="paymentType"
              ref="paymentForm"
              v-model="paymentType"
              :invoice-id="invoice.invoice_id"
              :job-id="invoice.job_id"
              :customer="billingCustomer"
              :payment-amount-cents="paymentAmountCents"
              :financing-enabled="financingEnabled"
              :chromeless="true"
              data-cy="pay-modal-payment-methods"
            />
          </div>
        </template>
      </section>
    </div>

    <template #footer>
      <StickyConfirmFooter
        tone="pay"
        :amount-cents="paymentAmountCents"
        :destination="confirmDestination"
        :saving="saving"
        :disabled="disabled || !paymentType"
        @submit="handleApply"
        @cancel="$emit('hide-modal')"
      />
    </template>
  </SeranovaModal>
</template>

<script lang="ts">
import { mapActions, mapGetters } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { trackEvent } from "@/lib/pendo";

import InlineNote from "@/components/PaymentModals/InlineNote.vue";
import MethodTileGroup, {
  type MethodTile
} from "@/components/PaymentModals/MethodTileGroup.vue";
import MoneyAmountField from "@/components/PaymentModals/MoneyAmountField.vue";
import StickyConfirmFooter from "@/components/PaymentModals/StickyConfirmFooter.vue";
import SeranovaModal from "@/components/Seranova/Modal/Modal.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import { dollars } from "@/mixins";
import type { Customer } from "@/typings/customer";
import type { Invoice } from "@/typings/invoice";
import Spinner from "../components/Spinner.vue";
import PaymentMethods from "./PaymentMethods.vue";
import usePaymentMethods from "./usePaymentMethods";

const METHOD_LABELS: Record<string, string> = {
  payment_method: "On file",
  gpi_card: "New card",
  stax_card: "New card",
  gpi_ach: "ACH",
  stax_ach: "ACH",
  cash: "Cash",
  check: "Check",
  external: "External"
};

const METHOD_ICONS: Record<string, string> = {
  payment_method: "fa-regular fa-credit-card",
  gpi_card: "fa-regular fa-credit-card",
  stax_card: "fa-regular fa-credit-card",
  gpi_ach: "fa-regular fa-building-columns",
  stax_ach: "fa-regular fa-building-columns",
  cash: "fa-regular fa-money-bill-wave",
  check: "fa-regular fa-money-check",
  external: "fa-regular fa-arrow-up-right-from-square"
};

export default {
  name: "AcceptPaymentV2",
  components: {
    InlineNote,
    MethodTileGroup,
    MoneyAmountField,
    PaymentMethods,
    SeranovaModal,
    Spinner,
    StickyConfirmFooter,
    Toggle
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    invoice: {
      type: Object as () => Invoice,
      default: null
    }
  },
  emits: ["payment-success", "payment-method-updated", "hide-modal"],
  setup() {
    const {
      paymentMethodsResponse,
      fetchPaymentMethods,
      fetchPaymentReceivers,
      fetchCustomer,
      paymentAmountErrorMessage
    } = usePaymentMethods();
    return {
      paymentMethodsResponse,
      fetchPaymentMethods,
      fetchPaymentReceivers,
      fetchCustomer,
      paymentAmountErrorMessage
    };
  },
  data() {
    return {
      saving: false,
      loadingMethods: true,
      paymentType: "" as string,
      paymentAmountCents: 0,
      applyCredit: false,
      amountInitialized: false
    };
  },
  computed: {
    ...mapGetters(["financingEnabled"]),
    billingCustomer({ invoice }: any): Customer | null {
      return invoice?.billing_customer ?? null;
    },
    balanceCents({ invoice }: any): number {
      return invoice?.financials?.balance_cents ?? 0;
    },
    progressBilling({ invoice }: any) {
      const spb = invoice?.progress_billing;
      if (!spb || spb.cancelled) return null;
      return spb.is_progress_billing && spb.current_draw ? spb : null;
    },
    currentDrawCents(): number {
      return this.progressBilling?.current_draw?.remaining_cents ?? 0;
    },
    applyableCreditCents(): number {
      return this.progressBilling?.applyable_credit_cents ?? 0;
    },
    hasApplyableCredit(): boolean {
      return this.applyableCreditCents > 0;
    },
    isLastDraw(): boolean {
      return (
        !!this.progressBilling &&
        this.progressBilling.next_draw_after_current == null
      );
    },
    mustApplyCredit(): boolean {
      return this.isLastDraw && this.hasApplyableCredit;
    },
    methodTiles(): MethodTile[] {
      return (this.paymentMethodsResponse || []).map((m: any) => {
        const label = METHOD_LABELS[m.key] ?? m.label ?? m.key;
        const icon = METHOD_ICONS[m.key];
        const disabled =
          m.key === "payment_method" && !this.billingCustomer?.payment_method;
        return { id: m.key, label, icon, disabled };
      });
    },
    amountHint(): string {
      if (this.progressBilling) return "Pre-filled with the current draw.";
      return "Pre-filled with the invoice balance.";
    },
    amountWarning(): string {
      const v = this.paymentAmountCents;
      if (v <= 0) return "";
      const credit = this.applyCredit ? this.applyableCreditCents : 0;
      const effective = v + credit;
      const draw = this.currentDrawCents;
      const balance = this.balanceCents;

      if (this.progressBilling && effective > draw && effective <= balance) {
        return `This payment <strong>exceeds the current draw</strong> (${dollars(
          draw
        )}). The overage will roll forward to the next draw when it becomes due.`;
      }
      if (effective > balance && balance > 0) {
        return `This <strong>exceeds the invoice balance</strong> (${dollars(
          balance
        )}). Lower the amount or the customer will overpay.`;
      }
      if (this.progressBilling && effective < draw) {
        return `This is <strong>less than the current draw</strong>. ${dollars(
          draw - effective
        )} of this draw will remain due.`;
      }
      return "";
    },
    confirmDestination(): string {
      if (!this.paymentType) return "";
      if (this.paymentType === "payment_method") {
        const pm = this.billingCustomer?.payment_method;
        if (pm?.brand && pm?.last4) return `${pm.brand} •••• ${pm.last4}`;
        return "method on file";
      }
      if (this.paymentType === "gpi_card" || this.paymentType === "stax_card")
        return "a new card";
      if (this.paymentType === "gpi_ach" || this.paymentType === "stax_ach")
        return "ACH transfer";
      if (this.paymentType === "cash") return "cash";
      if (this.paymentType === "check") return "check";
      if (this.paymentType === "external") return "an external processor";
      return "";
    }
  },
  watch: {
    invoice: {
      immediate: true,
      deep: true,
      handler(newInvoice: Invoice) {
        if (!newInvoice || this.amountInitialized) return;
        this.amountInitialized = true;
        const spb = newInvoice.progress_billing;
        const planActive =
          spb?.is_progress_billing && !spb.cancelled && spb.current_draw;
        const lastDraw = planActive && spb!.next_draw_after_current == null;
        const credit = spb?.applyable_credit_cents ?? 0;
        const forceCredit = lastDraw && credit > 0;
        this.applyCredit = forceCredit;
        if (planActive) {
          const remaining = spb!.current_draw!.remaining_cents;
          this.paymentAmountCents = forceCredit
            ? Math.max(remaining - credit, 1)
            : remaining;
        } else {
          this.paymentAmountCents = newInvoice.financials?.balance_cents ?? 0;
        }
      }
    },
    applyCredit(checked: boolean) {
      if (!this.progressBilling) return;
      const drawRemaining = this.currentDrawCents;
      if (checked) {
        this.paymentAmountCents = Math.max(
          drawRemaining - this.applyableCreditCents,
          1
        );
      } else {
        this.paymentAmountCents = drawRemaining;
      }
    },
    paymentMethodsResponse: {
      immediate: true,
      handler(next: any[]) {
        if (!next || next.length === 0) return;
        if (this.paymentType) return;
        const enabledFirst = next.find(
          (m: any) =>
            !(
              m.key === "payment_method" &&
              !this.billingCustomer?.payment_method
            )
        );
        if (enabledFirst) this.paymentType = enabledFirst.key;
      }
    }
  },
  async mounted() {
    if (!this.invoice) return;
    this.fetchPaymentReceivers(this.invoice.payment_receivers_url);
    this.fetchTransactions(this.invoice);
    if (this.progressBilling) {
      trackEvent("spb_accept_payment_modal_opened", {
        invoice_id: this.invoice.invoice_id,
        payment_status_code: this.progressBilling.payment_status,
        current_draw_label: this.progressBilling.current_draw.label
      });
    }
    // Fetch the customer's saved methods + the available method types so
    // the MethodTileGroup can render its tiles. V1 lets PaymentMethods
    // own this, but V2's tile group is the parent — we have to fetch up
    // here ourselves.
    if (this.billingCustomer) {
      await this.fetchCustomer(this.billingCustomer.customer_id);
    }
    await this.fetchPaymentMethods();
    this.loadingMethods = false;
  },
  methods: {
    ...mapActions(["fetchTransactions", "createTransaction"]),
    dollars,
    formatSpbDueDate(isoDate: string): string {
      if (!isoDate) return "";
      const [y, m, d] = isoDate.split("-");
      return `${m}/${d}/${y}`;
    },
    async handleApply() {
      if (this.saving) return;
      if (this.paymentAmountCents < 1) {
        window.notify?.error?.("Payment amount must be at least 1 cent.");
        return;
      }
      this.saving = true;
      try {
        const formData = await (
          this.$refs.paymentForm as { getData: () => Promise<any> }
        )?.getData();
        if (!formData) {
          this.saving = false;
          return;
        }
        const data = {
          amount_cents: this.paymentAmountCents,
          ...formData,
          ...(this.applyCredit ? { apply_credit: true } : {})
        };
        await this.createTransaction({ invoice: this.invoice, data });
        if (this.progressBilling && !this.progressBilling.current_draw.is_due) {
          trackEvent("spb_early_payment_recorded", {
            invoice_id: this.invoice.invoice_id,
            current_draw_label: this.progressBilling.current_draw.label,
            amount_cents: this.paymentAmountCents,
            payment_status_code: this.progressBilling.payment_status
          });
        }
        window.notify?.success?.("Payment processed successfully!");
        this.$emit("payment-success");
        this.$emit("hide-modal");
        if (
          formData.card_save_payment_method ||
          formData.ach_save_payment_method
        ) {
          EventBus.$emit("payment-method-updated");
        }
      } catch (err) {
        // createTransaction surfaces its own user-facing error toast;
        // we keep the modal open so the user can retry without losing input.
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss">
// Legacy TextField renders its root as a <label class="textfield-label">,
// which the global stylesheet sets to display: inline-block. Two siblings
// in ExternalOtherForm end up rendering side-by-side and the second one
// overflows the modal. Force the legacy form labels to block-display when
// they appear inside the V2 modal so siblings stack and respect the
// parent width.
.acceptPaymentV2 {
  .externalPaymentForm,
  .externalPaymentForm > .full-width {
    width: 100% !important;
    box-sizing: border-box;
  }
  // Each TextField root is a <label class="textfield-label"> — block-stack
  // them inside the External form so the second field doesn't overflow.
  .externalPaymentForm .textfield-label {
    display: block !important;
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}
</style>
